import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  RefreshCw,
  Mail,
  Phone,
  Building2,
  Clock,
  X,
  Trash2,
  Save,
  ExternalLink,
  AlertCircle,
} from "lucide-react";
import {
  adminListLeads,
  adminUpdateLead,
  adminDeleteLead,
} from "../../lib/api";

const STATUSES = [
  { key: "all", label: "Alle" },
  { key: "neu", label: "Neu" },
  { key: "in_bearbeitung", label: "In Bearbeitung" },
  { key: "beantwortet", label: "Beantwortet" },
  { key: "abgeschlossen", label: "Abgeschlossen" },
];

const statusMeta = {
  neu: { label: "Neu", color: "text-pink-400", bg: "bg-pink-500/10 border-pink-500/30" },
  in_bearbeitung: { label: "In Bearbeitung", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30" },
  beantwortet: { label: "Beantwortet", color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/30" },
  abgeschlossen: { label: "Abgeschlossen", color: "text-green-400", bg: "bg-green-500/10 border-green-500/30" },
};

const LeadDetail = ({ lead, onClose, onChanged }) => {
  const [status, setStatus] = useState(lead.status);
  const [notes, setNotes] = useState(lead.notes || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const updated = await adminUpdateLead(lead.id, { status, notes });
      onChanged(updated);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Anfrage wirklich löschen?")) return;
    setSaving(true);
    try {
      await adminDeleteLead(lead.id);
      onChanged(null, true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Löschen fehlgeschlagen");
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end lg:items-center justify-center p-0 lg:p-6"
      onClick={onClose}
      data-testid="lead-detail-overlay"
    >
      <div
        className="w-full lg:max-w-2xl bg-neutral-950 border border-white/10 rounded-t-3xl lg:rounded-3xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-neutral-950 px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-pink-400">
              Anfrage
            </div>
            <div className="font-display font-extrabold text-xl text-white truncate">
              {lead.name}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white"
            data-testid="lead-detail-close-btn"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <a
              href={`mailto:${lead.email}`}
              className="flex items-center gap-3 p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-pink-400/40"
            >
              <Mail size={16} className="text-pink-400" />
              <div className="text-sm text-white truncate">{lead.email}</div>
            </a>
            {lead.phone && (
              <a
                href={`tel:${lead.phone}`}
                className="flex items-center gap-3 p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-pink-400/40"
              >
                <Phone size={16} className="text-orange-400" />
                <div className="text-sm text-white truncate">{lead.phone}</div>
              </a>
            )}
            {lead.company && (
              <div className="flex items-center gap-3 p-4 rounded-2xl bg-white/5 border border-white/10">
                <Building2 size={16} className="text-purple-400" />
                <div className="text-sm text-white truncate">{lead.company}</div>
              </div>
            )}
            <div className="flex items-center gap-3 p-4 rounded-2xl bg-white/5 border border-white/10">
              <Clock size={16} className="text-neutral-400" />
              <div className="text-sm text-white">
                {new Date(lead.created_at).toLocaleString("de-DE")}
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">
                Leistung
              </div>
              <div className="mt-1 text-sm text-white">{lead.leistung || "–"}</div>
            </div>
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">
                Budget
              </div>
              <div className="mt-1 text-sm text-white">{lead.budget || "–"}</div>
            </div>
          </div>

          <div className="p-5 rounded-2xl bg-white/5 border border-white/10">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">
              Nachricht
            </div>
            <div className="text-sm text-neutral-100 leading-relaxed whitespace-pre-wrap">
              {lead.message}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">
              Status
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(statusMeta).map(([key, meta]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setStatus(key)}
                  className={`px-4 py-2 rounded-full text-xs font-bold tracking-wider uppercase border transition-colors ${
                    status === key
                      ? `${meta.bg} ${meta.color}`
                      : "bg-white/5 border-white/10 text-neutral-400 hover:bg-white/10"
                  }`}
                  data-testid={`lead-status-btn-${key}`}
                >
                  {meta.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">
              Interne Notiz
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Interne Notizen (z. B. nächste Schritte, Vereinbarungen) …"
              className="w-full p-4 rounded-2xl bg-white/5 border border-white/10 focus:border-pink-400 focus:bg-white/10 outline-none text-white placeholder:text-neutral-500 resize-none text-sm"
              data-testid="lead-notes-textarea"
            />
          </div>

          {err && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm flex items-center gap-2">
              <AlertCircle size={14} /> {err}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              onClick={save}
              disabled={saving}
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 text-white text-sm font-bold uppercase tracking-wider hover:-translate-y-[2px] transition-all disabled:opacity-60"
              data-testid="lead-save-btn"
            >
              <Save size={14} />
              {saving ? "Speichern…" : "Speichern"}
            </button>
            <a
              href={`mailto:${lead.email}?subject=Re:%20Deine%20Anfrage%20bei%20FREEAYTO`}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white text-neutral-950 text-sm font-bold uppercase tracking-wider hover:bg-neutral-200 transition-colors"
            >
              <ExternalLink size={14} /> Antworten
            </a>
            <button
              onClick={remove}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-red-500/10 border border-red-500/30 text-red-400 text-sm font-bold uppercase tracking-wider hover:bg-red-500/20 transition-colors"
              data-testid="lead-delete-btn"
            >
              <Trash2 size={14} /> Löschen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const LeadsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await adminListLeads({ status: filter, q });
      setItems(data.items || []);
    } catch (e) {
      setErr("Anfragen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const counts = useMemo(() => {
    const c = { all: items.length };
    items.forEach((l) => {
      c[l.status] = (c[l.status] || 0) + 1;
    });
    return c;
  }, [items]);

  const onChanged = (updated, deleted = false) => {
    if (deleted) {
      setItems((prev) => prev.filter((l) => l.id !== active.id));
      setActive(null);
      return;
    }
    setItems((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    setActive(updated);
  };

  return (
    <div data-testid="admin-leads-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-pink-400">
            Posteingang
          </div>
          <h1 className="mt-1 font-display font-extrabold text-3xl md:text-5xl tracking-tight">
            Anfragen
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 md:w-72">
            <Search
              size={14}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500"
            />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Suche Name, E-Mail, Firma…"
              className="w-full pl-10 pr-4 py-3 rounded-full bg-white/5 border border-white/10 focus:border-pink-400 outline-none text-white placeholder:text-neutral-500 text-sm"
              data-testid="lead-search-input"
            />
          </div>
          <button
            onClick={load}
            className="p-3 rounded-full bg-white/5 border border-white/10 text-neutral-300 hover:bg-white/10"
            aria-label="Neu laden"
            data-testid="leads-refresh-btn"
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {STATUSES.map((s) => {
          const isActive = filter === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setFilter(s.key)}
              className={`px-4 py-2 rounded-full text-xs font-bold tracking-wider uppercase border transition-colors ${
                isActive
                  ? "bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 text-white border-transparent"
                  : "bg-white/5 border-white/10 text-neutral-300 hover:bg-white/10"
              }`}
              data-testid={`leads-filter-${s.key}`}
            >
              {s.label}
              {s.key !== "all" && counts[s.key] ? (
                <span className="ml-2 opacity-80">({counts[s.key]})</span>
              ) : null}
            </button>
          );
        })}
      </div>

      {err && (
        <div className="mb-4 p-4 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
          {err}
        </div>
      )}

      <div className="rounded-2xl bg-white/5 border border-white/10 divide-y divide-white/5">
        {loading && (
          <div className="p-8 text-center text-sm text-neutral-400">
            Laden…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-10 text-center">
            <div className="text-sm text-neutral-400">
              Keine Anfragen gefunden.
            </div>
          </div>
        )}
        {!loading &&
          items.map((lead) => {
            const meta = statusMeta[lead.status] || statusMeta.neu;
            return (
              <button
                key={lead.id}
                onClick={() => setActive(lead)}
                className="w-full text-left flex items-center justify-between gap-4 p-4 md:p-5 hover:bg-white/5 transition-colors"
                data-testid={`lead-row-${lead.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-bold text-white truncate">
                      {lead.name}
                    </div>
                    <span
                      className={`shrink-0 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${meta.bg} ${meta.color}`}
                    >
                      {meta.label}
                    </span>
                    {lead.leistung && (
                      <span className="shrink-0 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-white/5 text-neutral-300 border border-white/10">
                        {lead.leistung}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-neutral-400 truncate mt-1">
                    {lead.email}
                    {lead.company ? ` · ${lead.company}` : ""}
                  </div>
                  <div className="text-xs text-neutral-500 truncate mt-1">
                    {lead.message}
                  </div>
                </div>
                <div className="shrink-0 text-xs text-neutral-500 hidden sm:flex items-center gap-2">
                  <Clock size={12} />
                  {new Date(lead.created_at).toLocaleString("de-DE")}
                </div>
              </button>
            );
          })}
      </div>

      {active && (
        <LeadDetail
          lead={active}
          onClose={() => setActive(null)}
          onChanged={onChanged}
        />
      )}
    </div>
  );
};

export default LeadsPage;
